"use client";

import React, { useCallback, useEffect } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { motion, AnimatePresence } from "motion/react";
import { Upload, X, FileText, ImageIcon, AlertCircle } from "lucide-react";
import type { FileUploadProps } from "@/types/contact";
import s from "./contact.module.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ACCEPTED_TYPES: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isImageFile = (file: File) => file.type.startsWith("image/");

export function FileUpload({ onFileSelect, selectedFile, error }: FileUploadProps) {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [dropError, setDropError] = React.useState<string | null>(null);

  // Revoke object URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      setDropError(null);

      if (rejected.length > 0) {
        const code = rejected[0].errors[0]?.code;
        setDropError(
          code === "file-too-large"
            ? "File exceeds the 5 MB limit."
            : code === "file-invalid-type"
              ? "Unsupported format. Use JPG, PNG, WebP or PDF."
              : "This file could not be uploaded."
        );
        return;
      }

      const file = accepted[0];
      if (!file) return;

      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return isImageFile(file) ? URL.createObjectURL(file) : null;
      });

      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setDropError(null);
    onFileSelect(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const displayError = dropError ?? error;

  const dropClass = [
    s.dropzone,
    isDragActive ? s.dropzoneActive : "",
    displayError ? s.dropzoneError : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={s.field}>
      <span className={s.label}>
        Reference File
        <span className={s.optional}>Optional</span>
      </span>

      <AnimatePresence mode="wait" initial={false}>
        {selectedFile ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className={s.preview}
          >
            {previewUrl ? (
              <div className={s.previewImgWrap}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={previewUrl} alt="Uploaded reference" className={s.previewImg} />
                <div className={s.previewScrim} />
                <div className={s.previewMeta}>
                  <p className={s.previewName}>{selectedFile.name}</p>
                  <p className={s.previewSize}>{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
            ) : (
              <div className={s.previewDoc}>
                <span className={s.docIcon}>
                  <FileText size={19} strokeWidth={1.7} />
                </span>
                <div className={s.docText}>
                  <p className={s.docName}>{selectedFile.name}</p>
                  <p className={s.docSize}>{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleRemove}
              className={s.removeBtn}
              aria-label="Remove uploaded file"
            >
              <X size={14} strokeWidth={2.3} />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              {...getRootProps({ className: dropClass })}
              aria-label="Upload reference file. JPG, PNG, WebP or PDF. Maximum 5 MB."
            >
              <input {...getInputProps()} />

              <span className={s.dropIcon}>
                {isDragActive ? (
                  <ImageIcon size={20} strokeWidth={1.6} />
                ) : (
                  <Upload size={20} strokeWidth={1.6} />
                )}
              </span>

              <div>
                <p className={s.dropTitle}>
                  {isDragActive ? "Release to attach" : "Drag & drop, or browse files"}
                </p>
                <p className={s.dropHint}>JPG · PNG · WEBP · PDF — up to 5 MB</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {displayError && (
          <motion.div
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -4, height: 0 }}
            transition={{ duration: 0.22 }}
            className={s.errorMsg}
            role="alert"
            aria-live="polite"
          >
            <AlertCircle size={13} strokeWidth={2.4} aria-hidden="true" />
            <span>{displayError}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}